import {
  IResource,
  Office,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE,
  WL_PRESENCE_OFFICE,
  WL_NOT_AVAILABLE
} from "@gettactic/api";
import {
  AvailableStatus,
  OfficeIcon,
  RBStatusIcon
} from "@cmp-src/icons/TacticIcons/statusIcons";
import { CampusIcon } from "@cmp-src/icons/TacticIcons/CampusIcon";
import { Dict } from "@gettactic/helpers/src/types/common";
import { cn } from "@gettactic/helpers";

export interface WorkingLocationPresenceIconProps {
  status: string;
  isRB?: boolean;
  className?: string;
  /**
   * If true and there's a reservation (resource assigned), show CampusIcon
   * Used in directory view to indicate any office reservation
   */
  showCampusIconForReservation?: boolean;
  /**
   * Whether there's a reservation (resource assigned) for this working location
   * Used with showCampusIconForReservation prop
   */
  hasReservation?: boolean;
}

export interface FullLocationType {
  location: string;
  policy_type: string;
  office?: Office;
  resource?: IResource;
}

export function getWorkingLocationStatus({
  filterOfficeId,
  officeIdOrOtherLocation,
  locationIsAssigned,
  assignedLocation,
  assignedOfficeId,
  assignedResource,
  assignedPolicyType
}: {
  filterOfficeId: string | string[] | null;
  officeIdOrOtherLocation: string;
  locationIsAssigned: boolean;
  assignedLocation: string;
  assignedOfficeId: string | null;
  assignedResource: IResource | undefined;
  assignedPolicyType: string | null;
}): (typeof AvailableStatus)[keyof typeof AvailableStatus] {
  if (!locationIsAssigned) {
    return AvailableStatus.unknown;
  }

  if (
    assignedLocation === WL_PRESENCE_REMOTE ||
    (assignedLocation === WL_PRESENCE_OOO && assignedPolicyType === "remote")
  ) {
    return AvailableStatus.remote;
  }

  if (assignedLocation === WL_PRESENCE_OOO) {
    return AvailableStatus.ooo;
  }

  if (assignedLocation === WL_PRESENCE_OFFICE) {
    if (!assignedResource) {
      return AvailableStatus.reservation;
    }

    // TBD: enable when available
    // return AvailableStatus.inAnotherOffice;
  }

  if (
    filterOfficeId &&
    assignedOfficeId &&
    !filterOfficeId.includes(assignedOfficeId)
  ) {
    return AvailableStatus.inAnotherOffice;
  }

  if (assignedLocation === WL_PRESENCE_OFFICE) {
    if (assignedResource) {
      return AvailableStatus.inOffice;
    }
    return AvailableStatus.reservation;
  }

  if (officeIdOrOtherLocation === "") {
    return AvailableStatus.unknown;
  }

  if (
    officeIdOrOtherLocation === WL_PRESENCE_OOO ||
    officeIdOrOtherLocation === WL_NOT_AVAILABLE
  ) {
    return AvailableStatus.ooo;
  }

  if (assignedLocation === WL_PRESENCE_REMOTE) {
    return AvailableStatus.remote;
  }

  if (locationIsAssigned) {
    if (!assignedOfficeId) {
      return AvailableStatus.unknown;
    }

    if (filterOfficeId && !filterOfficeId.includes(assignedOfficeId || "")) {
      return AvailableStatus.inAnotherOffice;
    }
    return AvailableStatus.inOffice;
  }

  return AvailableStatus.unknown;
}

const incompatibleStatuses = {
  // for the Redbull office icon is based on office location
  [AvailableStatus.inOffice]: null,
  [AvailableStatus.remote]: "home",
  [AvailableStatus.unknown]: "no"
} as Dict;

const paleStatuses = [
  AvailableStatus.ooo,
  AvailableStatus.unknown,
  AvailableStatus.remote,
  AvailableStatus.inAnotherOffice
];

export function WorkingLocationPresenceIcon({
  status,
  className = "h-[16px] w-[16px]",
  isRB = false,
  showCampusIconForReservation = false,
  hasReservation = false
}: WorkingLocationPresenceIconProps) {
  // Show CampusIcon in directory view when there's a reservation in any office
  if (showCampusIconForReservation && hasReservation) {
    return <CampusIcon className={cn("text-icon-gray", className)} />;
  }

  // TBD: make reference table for types
  if (isRB) {
    // TBD: Implement the logic for the Redbull office icon
    const region =
      status in incompatibleStatuses
        ? incompatibleStatuses[status]
          ? incompatibleStatuses[status]
          : "Auckland"
        : status;

    const Icon = (RBStatusIcon as Dict)[status];
    if (!Icon) {
      console.error(`Icon for status ${status} not found`);
      return null;
    }
    return <Icon region={region} />;
  }
  const isPale = paleStatuses.includes(status as (typeof paleStatuses)[number]);
  const Icon = (OfficeIcon as Dict)[status];

  // keep className at the end
  return (
    <Icon
      className={cn(
        isPale ? "text-icon-paleGray" : "text-icon-gray",
        className
      )}
    />
  );
}
